import { FC, useState, useEffect } from "react";
import {
  Box,
  Dialog,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import SearchIcon from "@mui/icons-material/Search";
import "./NotificationsPage.scss";
import CommonGifAnimation from "../../../components/CustomGifAnimation/CommonGifAnimation";
import {
  E_ChannelOptions,
  T_AnimationData,
} from "../../../globalTypes/GlobalTypes";
import { NotificationsData } from "../GeneralAppElements_Types";

import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import CloseIcon from "@mui/icons-material/Close";
import { Transition } from "../HamburgerMenuDialog/HamburgerMenuDialog";
import clevertap from "../../../utils/CleverTap";
import NotFoundGif from "../../../assets/gif/Not_found.json";
import NotificationDrawer from "./NotificationDrawer";
import CommonSearchableScreen from "./CommonSearchableScreen";
import { useAppSelector } from "../../../redux-store/CustomReduxHooks";

interface CommonProps {
  title: string;
  notificationListData: NotificationsData[];
}

interface Props {
  className?: string;
  open: boolean;
  handleOpen: Function;
}

const NotificationsPage: FC<Props> = ({ open, handleOpen, className }) => {
  const [openNotificationDrawer, setOpenNotificationDrawer] =
    useState<boolean>(false);
  const [activeDashboardTab, setActiveDashboardTab] = useState<string>("All");
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [searchResult, setSearchResult] = useState<boolean>(true);
  const [notificationNewData, setNotificationNewData] = useState<
    NotificationsData[]
  >([]);
  const [todayNotifications, setTodayNotification] = useState<
    NotificationsData[]
  >([]);
  const [oldNotifications, setOldNotification] = useState<NotificationsData[]>(
    []
  );
  const csAppSlice = useAppSelector((state) => state.CsAppSlice);
  const isSuperApp = csAppSlice.channel === E_ChannelOptions.SUPER_APP;

  let currentTime = new Date().getTime(); // Current timestamp in milliseconds
  let twentyFourHoursAgo = currentTime - 24 * 60 * 60 * 1000;

  useEffect(() => {
    getAllInboxMessages();
  }, []);

  const getAllInboxMessages = () => {
    let result = clevertap.getAllInboxMessages();
    console.log(result);
    if (Object.keys(result).length === 0) {
      setSearchResult(false);
    } else {
      setSearchResult(true);
    }
    const notificationData = Object.keys(result).map((key: any) => {
      const noticeDataObj: NotificationsData = {
        preIcon: "",
        title: "",
        description: "",
        date: new Date(),
        isNotificationUnRead: false,
        tags: [],
        id: "",
        buttons: [],
      };
      let latestDate = new Date(result[key].date);
      noticeDataObj.title = result[key].msg[0].title;
      noticeDataObj.description = result[key].msg[0].description;
      noticeDataObj.preIcon = result[key].msg[0].iconUrl;
      noticeDataObj.date = latestDate;
      noticeDataObj.buttons = result[key].msg[0].buttons;
      noticeDataObj.isNotificationUnRead =
        result[key].viewed === 0 ? true : false;
      noticeDataObj.tags = result[key].tags;
      noticeDataObj.id = result[key].id;
      return noticeDataObj;
    });
    setNotificationNewData(notificationData);
    setTodayNotification(
      notificationData.filter(
        (notification) =>
          notification.date.getTime() >= twentyFourHoursAgo &&
          notification.date.getTime() <= currentTime
      )
    );
    setOldNotification(
      notificationData.filter(
        (notification) => notification.date.getTime() < twentyFourHoursAgo
      )
    );
  };
  const handleNotificationDrawer = () => {
    setOpenNotificationDrawer(true);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    let data = notificationNewData.filter(
      (notice) =>
        notice.title.toLowerCase().includes(event.target.value.toLowerCase()) ||
        notice.description
          .toLowerCase()
          .includes(event.target.value.toLowerCase())
    );
    if (data.length > 0) {
      setSearchResult(true);
    } else {
      setSearchResult(false);
    }
  };

  const handleNotificationDataChange = () => {
    getAllInboxMessages();
  };

  const handleUnreadBtnClick = () => {
    let unreadMsgCount = notificationNewData.filter(
      (data) => data.isNotificationUnRead === true
    );
    if (unreadMsgCount.length !== 0) {
      clevertap.markReadAllInboxMessage();
    }
    handleNotificationDataChange();
  };
  const handleDashboardChange = (newValue: string) => {
    setActiveDashboardTab(newValue);
    let todaysData = notificationNewData.filter(
      (notification) =>
        notification.date.toDateString() === new Date().toDateString()
    );
    let oldData = notificationNewData.filter(
      (notification) =>
        notification.date.toDateString() !== new Date().toDateString()
    );
    if (newValue !== "All") {
      setTodayNotification(
        todaysData.filter((item) => item.tags.includes(newValue))
      );
      setOldNotification(
        oldData.filter((item) => item.tags.includes(newValue))
      );
    } else {
      setTodayNotification(todaysData);
      setOldNotification(oldData);
    }
  };

  const GetSearchableScreens = (
    <>
      <Stack spacing={"1.5rem"} marginTop={"1.5rem"}>
        <CommonSearchableScreen
          title={"Today"}
          notificationListData={todayNotifications}
          handleNotificationDrawer={handleNotificationDrawer}
          handleNotificationDataChange={handleNotificationDataChange}
          handleUnreadBtnClick={handleUnreadBtnClick}
        />
        <CommonSearchableScreen
          title={"Older Notifications"}
          notificationListData={oldNotifications}
          handleNotificationDrawer={handleNotificationDrawer}
          handleNotificationDataChange={handleNotificationDataChange}
          handleUnreadBtnClick={handleUnreadBtnClick}
        />
      </Stack>
      <NotificationDrawer
        openData={openNotificationDrawer}
        handleOpenData={setOpenNotificationDrawer}
      />
    </>
  );

  const NotFoundComponent = (
    <Box className="not-found-component">
      <CommonGifAnimation animationData={NotFoundGif as T_AnimationData} />
      <Typography
        variant="h4"
        fontWeight={600}
        lineHeight={"1.1rem"}
        textAlign={"center"}
        color="rgba(46, 46, 46, 0.8)"
      >
        {"Uhh-Ohh!"}
      </Typography>
      <Typography
        variant="h6"
        lineHeight=".825rem"
        marginTop="1rem"
        color="rgba(46, 46, 46, 0.5)"
        textAlign="center"
      >
        {"No notifications found for your search query."}
      </Typography>
    </Box>
  );

  return (
    <Dialog
      className={`notification-dialog ${
        isSuperApp ? "no-tap-highlights" : ""
      } ${className}`}
      TransitionComponent={Transition}
      fullScreen
      open={open}
    >
      {className === "adaptive-notification" ? (
        <Box
          display="flex"
          margin="1.5rem 1.5rem 0"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h2" lineHeight="1.625rem">
            Notifications
          </Typography>
          <CloseIcon
            sx={{ color: "#757575", cursor: "pointer" }}
            fontSize="small"
            onClick={() => handleOpen(false)}
          />
        </Box>
      ) : (
        <Box className="header-section">
          <KeyboardArrowLeftIcon
            onClick={() => {
              if (
                searchResult === false &&
                searchTerm !== "" &&
                notificationNewData.length > 0
              ) {
                setSearchTerm("");
                handleDashboardChange("All");
                setSearchResult(true);
              } else {
                handleOpen(false);
              }
            }}
            className="back-arrow-icon"
          />
          <Typography variant="h3">Notifications</Typography>
        </Box>
      )}
      <Box className="content-wrapper">
        <Box className="notifications-page">
          <TextField
            className="search-field "
            fullWidth
            placeholder="Search for insurance"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          {searchResult && (
            <Tabs
              value={activeDashboardTab}
              onChange={(event, newValue) => handleDashboardChange(newValue)}
              sx={{ marginTop: "16px", minHeight: "30px", height: "30px" }}
            >
              <Tab
                label="All"
                value="All"
                sx={{
                  color: activeDashboardTab === "All" ? "#FFFFFF" : "#000000",
                  backgroundColor:
                    activeDashboardTab === "All" ? "#CA1F34" : "transparent",
                  fontSize: "12px",
                  minWidth: "auto", // Let width auto-adjust to content
                  maxWidth: "200px",
                  minHeight: "24px",
                  height: "24px",
                  padding: "0.4rem 0.75rem !important",
                  borderRadius: "48px",
                  "&.Mui-selected": {
                    color: "#FFFFFF",
                  },
                }}
              />
              <Tab
                label="Investment"
                value="Investment"
                sx={{
                  color:
                    activeDashboardTab === "Investment" ? "#FFFFFF" : "#000000",
                  backgroundColor:
                    activeDashboardTab === "Investment"
                      ? "#CA1F34"
                      : "transparent",
                  fontSize: "12px",
                  minWidth: "auto", // Let width auto-adjust to content
                  maxWidth: "200px",
                  minHeight: "24px",
                  height: "24px",
                  padding: "0.4rem 0.75rem !important",
                  borderRadius: "48px",
                  "&.Mui-selected": {
                    color: "#FFFFFF",
                  },
                }}
              />
              <Tab
                label="Insurance"
                value="Insurance"
                sx={{
                  color:
                    activeDashboardTab === "Insurance" ? "#FFFFFF" : "#000000",
                  backgroundColor:
                    activeDashboardTab === "Insurance"
                      ? "#CA1F34"
                      : "transparent",
                  fontSize: "12px",
                  minWidth: "auto", // Let width auto-adjust to content
                  maxWidth: "200px",
                  minHeight: "24px",
                  height: "24px",
                  padding: "0.4rem 0.75rem !important",
                  borderRadius: "48px",
                  "&.Mui-selected": {
                    color: "#FFFFFF",
                  },
                }}
              />
              <Tab
                label="Loans"
                value="Loans"
                sx={{
                  color: activeDashboardTab === "Loans" ? "#FFFFFF" : "#000000",
                  backgroundColor:
                    activeDashboardTab === "Loans" ? "#CA1F34" : "transparent",
                  fontSize: "12px",
                  minWidth: "auto", // Let width auto-adjust to content
                  maxWidth: "200px",
                  minHeight: "24px",
                  height: "24px",
                  padding: "0.4rem 0.75rem !important",
                  borderRadius: "48px",
                  "&.Mui-selected": {
                    color: "#FFFFFF",
                  },
                }}
              />
              <Tab
                label="Others"
                value="Others"
                sx={{
                  color:
                    activeDashboardTab === "Others" ? "#FFFFFF" : "#000000",
                  backgroundColor:
                    activeDashboardTab === "Others" ? "#CA1F34" : "transparent",
                  fontSize: "12px",
                  minWidth: "auto", // Let width auto-adjust to content
                  maxWidth: "200px",
                  minHeight: "24px",
                  height: "24px",
                  padding: "0.4rem 0.75rem !important",
                  borderRadius: "48px",
                  "&.Mui-selected": {
                    color: "#FFFFFF",
                  },
                }}
              />
            </Tabs>
          )}
          {searchResult ? GetSearchableScreens : NotFoundComponent}
        </Box>
      </Box>
    </Dialog>
  );
};

export default NotificationsPage;
